import React, { useMemo, useCallback, useRef } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

const PALETTE = ['#1e88e5', '#8e24aa', '#00897b', '#ef6c00', '#5d4037', '#3949ab', '#c62828'];

const formatXLabel = (ts, spanMs) => {
  const d = dayjs(ts);
  if (!d.isValid()) return String(ts || '').slice(0, 16);
  if (spanMs >= 12 * 60 * 60 * 1000) return d.format('DD MMM HH:mm');
  return d.format('HH:mm');
};

/**
 * Multi-sensor / multi-series overlay with shared crosshair (FR-01 / FR-05).
 * series: [{ key, label, color?, points: [{ timestamp, value }] }]
 */
const MultiSeriesLineChart = ({
  series = [],
  height = 220,
  unit = '',
  selectedTimestamp,
  onPointClick,
}) => {
  const svgRef = useRef(null);
  const width = 960;
  const padding = { top: 16, right: 72, bottom: 28, left: 48 };

  const built = useMemo(() => {
    const prepared = (series || [])
      .map((s, i) => {
        const pts = (s.points || [])
          .map((p) => ({
            timestamp: p.timestamp || p.t,
            t: dayjs(p.timestamp || p.t).valueOf(),
            value: Number(p.value),
          }))
          .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.value))
          .sort((a, b) => a.t - b.t);
        return {
          key: s.key || String(i),
          label: s.label || s.key || `Series ${i + 1}`,
          color: s.color || PALETTE[i % PALETTE.length],
          points: pts,
        };
      })
      .filter((s) => s.points.length);

    if (!prepared.length) return null;

    let tMin = Infinity;
    let tMax = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    prepared.forEach((s) => {
      s.points.forEach((p) => {
        tMin = Math.min(tMin, p.t);
        tMax = Math.max(tMax, p.t);
        minV = Math.min(minV, p.value);
        maxV = Math.max(maxV, p.value);
      });
    });
    if (minV === maxV) {
      minV -= 0.5;
      maxV += 0.5;
    }
    const yPad = (maxV - minV) * 0.12;
    minV -= yPad;
    maxV += yPad;
    const ySpan = maxV - minV || 1;
    const spanMs = Math.max(1, tMax - tMin);
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;
    const xAtT = (t) => padding.left + ((t - tMin) / spanMs) * innerW;
    const yAt = (v) => padding.top + innerH - ((v - minV) / ySpan) * innerH;

    const paths = prepared.map((s) => {
      const d = s.points
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAtT(p.t).toFixed(1)} ${yAt(p.value).toFixed(1)}`)
        .join(' ');
      return { ...s, d };
    });

    const ticks = [0, 0.25, 0.5, 0.75, 1].map((frac) => {
      const v = minV + ySpan * frac;
      return { y: yAt(v), label: v.toFixed(2) };
    });
    const xLabels = [0, 0.33, 0.66, 1].map((frac) => {
      const t = tMin + frac * spanMs;
      return { x: xAtT(t), label: formatXLabel(t, spanMs) };
    });

    const hitPoints = [];
    prepared.forEach((s) => {
      s.points.forEach((p) => {
        hitPoints.push({
          x: xAtT(p.t),
          y: yAt(p.value),
          timestamp: p.timestamp,
          value: p.value,
          seriesKey: s.key,
          color: s.color,
        });
      });
    });

    let cursorX = null;
    if (selectedTimestamp != null) {
      const target = dayjs(selectedTimestamp).valueOf();
      if (Number.isFinite(target)) {
        cursorX = xAtT(Math.min(tMax, Math.max(tMin, target)));
      }
    }

    return { paths, ticks, xLabels, hitPoints, cursorX, unit };
  }, [series, height, selectedTimestamp, padding.left, padding.right, padding.top, padding.bottom]);

  const handleClick = useCallback(
    (e) => {
      if (!onPointClick || !built?.hitPoints?.length || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const scale = Math.min(rect.width / width, rect.height / height);
      const offsetX = (rect.width - width * scale) / 2;
      const localX = (e.clientX - rect.left - offsetX) / scale;
      let best = null;
      let bestScore = Infinity;
      built.hitPoints.forEach((h) => {
        const dx = h.x - localX;
        const score = dx * dx;
        if (score < bestScore) {
          bestScore = score;
          best = h;
        }
      });
      if (best) onPointClick({ timestamp: best.timestamp, value: best.value, seriesKey: best.seriesKey });
    },
    [onPointClick, built, height]
  );

  if (!built) {
    return (
      <Typography variant="caption" color="text.secondary">
        No series to plot.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 0.5, mb: 0.5 }}>
        {built.paths.map((s) => (
          <Typography key={s.key} variant="caption" sx={{ color: s.color, fontWeight: 600 }}>
            ● {s.label}
          </Typography>
        ))}
        {unit ? (
          <Typography variant="caption" color="text.secondary">
            ({unit})
          </Typography>
        ) : null}
      </Stack>
      <Box sx={{ width: '100%', height, bgcolor: '#fafcfd', borderRadius: 1 }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          style={{ display: 'block', cursor: onPointClick ? 'crosshair' : 'default' }}
          onClick={handleClick}
        >
          {built.ticks.map((t) => (
            <g key={t.y}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={t.y}
                y2={t.y}
                stroke="#e8eef2"
              />
              <text x={padding.left - 6} y={t.y + 3} textAnchor="end" fontSize="10" fill="#78909c">
                {t.label}
              </text>
            </g>
          ))}
          {built.xLabels.map((l) => (
            <text key={l.x} x={l.x} y={height - 8} textAnchor="middle" fontSize="10" fill="#78909c">
              {l.label}
            </text>
          ))}
          {built.paths.map((s) => (
            <path key={s.key} d={s.d} fill="none" stroke={s.color} strokeWidth={1.75} />
          ))}
          {built.cursorX != null ? (
            <line
              x1={built.cursorX}
              x2={built.cursorX}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#455a64"
              strokeDasharray="4 3"
              strokeWidth={1}
            />
          ) : null}
        </svg>
      </Box>
    </Box>
  );
};

export default MultiSeriesLineChart;
